"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import React from "react";
import { ReservationStoryblok } from "@/component-types-sb";
import Title from "@/components/Title";
import FormGuard from "@/components/FormGuard";
import { storyblokEditable } from "@storyblok/react/rsc";

const ContactForm = ({ blok }: Readonly<{blok: ReservationStoryblok}>) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccess(null);
    setError(null);

    // Values only: the server knows the recipient and writes the mail.
    const formData = new FormData(e.currentTarget);
    formData.set("kind", "message");

    try {
      const emailResponse = await fetch("/api/sendEmail", {
        method: "POST",
        body: formData,
      });

      const result = await emailResponse.json().catch(() => ({}));

      if (!emailResponse.ok) {
        throw new Error(result.message || "Es gab ein Problem beim Senden der E-Mail.");
      }

      setSuccess("Ihre Nachricht wurde erfolgreich übermittelt!");
      (e.target as HTMLFormElement).reset();
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message ||
              "Es gab ein Problem bei der Übermittlung. Bitte versuchen Sie es erneut."
          : "Unbekannter Fehler"
      );
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div {...storyblokEditable(blok)} className="flex justify-center flex-col items-center pb-10">
        <Title variant="h3" className="text-2xl font-bold mb-8 text-left lg:text-center w-full lg:max-w-4xl">{blok.title}</Title>
        {success ? (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 lg:max-w-2xl">
            <p className="text-green-800">{success}</p>
          </div>
        ) : (
          <form className="grid gap-4 w-full lg:max-w-2xl" onSubmit={handleSubmit}>
            <div className="grid gap-2">
              <Label htmlFor="contact-name">Name *</Label>
              <Input
                id="contact-name"
                name="name"
                type="text"
                placeholder="Name"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contact-phone">Telefon</Label>
              <Input
                id="contact-phone"
                name="phone"
                type="tel"
                placeholder="Telefon"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contact-email">Email *</Label>
              <Input
                id="contact-email"
                name="email"
                type="email"
                placeholder="Email"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contact-message">Nachricht *</Label>
              <Textarea
                id="contact-message"
                name="message"
                placeholder="Nachricht"
                rows={3}
                className="resize-none"
                required
              />
            </div>

            <FormGuard />

            <Button type="submit" variant="default" disabled={isSubmitting}>
              {isSubmitting ? "Wird gesendet..." : "Abschicken"}
            </Button>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-800">{error}</p>
              </div>
            )}
          </form>
        )}
      </div>
  );
};

export default ContactForm;
